import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../chat/api";
import CopilotSidePanel from "./CopilotSidePanel";

/* The project, alongside the conversation instead of instead of it.
 *
 * "Open project" used to be a full navigation, which threw away the context the
 * user had just built — the project they made, what the copilot said to do next,
 * the thread itself. This keeps all of that on screen.
 *
 * Deliberately NOT the project page in a split. That page has its own tabs,
 * toolbar and tables; at 400px it breaks, and embedding it would put two
 * navigation systems on one screen so nobody knows what "back" means. This is a
 * purpose-built view of the one thing people come back to check — what is in the
 * project and whether it is readable yet — with a door to the real page.
 *
 * Since the in-chat upload card was removed this is also THE place documents are
 * added, so it behaves like the project table: a file appears the instant it is
 * chosen, and its status moves on its own from there.
 *
 * It reads /copilot/files/, which shares its readiness rule with the copilot's
 * own tools. Two surfaces disagreeing about whether a document is ready is the
 * specific failure that shares the rule rather than copying it.
 */

// Matches the cadence the ingestion pipeline writes status at. Slower and the
// bar jumps in visible steps; faster and it is polling for nothing.
const POLL_MS = 2500;
// How long we keep waiting on an action before giving up on it. A task that
// never runs would otherwise leave the row on 'Starting…' and the panel
// polling for ever — the exact failure this replaced, just slower.
const ACTION_TIMEOUT_MS = 90000;

/* One bar, two meters.
 *
 * Bytes-sent and the server's pipeline stage measure different things on
 * different scales, and showing them one after another in the same bar made it
 * run to 99% and then drop back to 20 — the transfer finishing looked like the
 * work regressing. Sending owns the first slice of the bar and the pipeline owns
 * the rest, so handing over always moves forwards.
 *
 * The share is small on purpose: transfer is usually the quick part, and the
 * minutes are spent reading the document afterwards. */
const TRANSFER_SHARE = 20;
// The same cap the Knowledge Base upload enforces. It is a client-side rule
// there too — the endpoint accepts more — so it is repeated rather than shared,
// and stated to the user rather than silently trimming their selection.
const MAX_PER_UPLOAD = 5;
/* The panel's own motifs — deliberately NOT the welcome screen's.
 *
 * That set is about the RFP lifecycle: submit, win, sign. This panel is about
 * one thing, holding documents, so it draws the desk rather than the journey —
 * sheets, a stack, a folder, a clip, an upload. Reusing the greeting's icons
 * here made the panel look like a smaller copy of the home page instead of its
 * own surface.
 *
 * They are also drawn differently: heavier stroke, and each one TILTED, so they
 * read as papers dropped on a surface rather than icons placed on a grid. The
 * welcome screen's glyphs sit upright; these lean. */
const PANEL_STROKE = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const PANEL_ICONS = {
  sheet: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <polyline points="14 3 14 8 19 8" />
      <line x1="8.5" y1="13" x2="15.5" y2="13" />
      <line x1="8.5" y1="16.5" x2="13" y2="16.5" />
    </>
  ),
  stack: (
    <>
      <rect x="3" y="7" width="13" height="14" rx="2" />
      <path d="M7 4h9a2 2 0 0 1 2 2v11" />
      <path d="M10.5 4.5h9.5" />
    </>
  ),
  folder: (
    <>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2.5h8a2 2 0 0 1 2 2V18a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M3 11h18" />
    </>
  ),
  clip: <path d="M20 11.5 11.2 20a5 5 0 0 1-7-7l9-8.7a3.3 3.3 0 0 1 4.7 4.7l-8.8 8.6a1.7 1.7 0 0 1-2.4-2.4l8-7.8" />,
  upload: (
    <>
      <path d="M12 16V7" />
      <path d="M8.5 10.5 12 7l3.5 3.5" />
      <path d="M5 16.5A4 4 0 0 1 6 8.7a5.5 5.5 0 0 1 10.6-1A3.8 3.8 0 0 1 19 15.9" />
    </>
  ),
};

/* Placed where the list ISN'T.
 *
 * Rows are opaque, so anything behind them is simply not seen — and the list
 * grows downward from the header. Glyphs spread over the full height therefore
 * spent most of their number hidden under files, leaving one lonely shape at
 * the bottom. They sit in the lower half, which is empty on the common case of
 * a project holding a handful of documents.
 *
 * Anchored to the BOTTOM so they hold their place as the panel grows and
 * shrinks instead of sliding under the list. The lowest clears the footer,
 * which is opaque and would slice it.
 *
 * Alpha tops out at 0.24. Above that the empty state's own body text drops
 * below 4.5:1 where the two can meet — measured, not guessed. */
const PANEL_GLYPHS = [
  { id: "a", icon: "sheet",  pos: "left-6 bottom-[50%]",  size: 40, tilt: "-12deg", color: "rgba(160,74,151,0.24)", float: "animate-sensa-sway",  delay: "0s" },
  { id: "b", icon: "clip",   pos: "right-7 bottom-[39%]", size: 30, tilt: "14deg",  color: "rgba(200,107,79,0.24)", float: "animate-sensa-sway-b", delay: "1.4s" },
  { id: "c", icon: "stack",  pos: "left-9 bottom-[28%]",  size: 34, tilt: "8deg",   color: "rgba(139,63,131,0.22)", float: "animate-sensa-sway-b", delay: "2.6s" },
  { id: "d", icon: "upload", pos: "right-6 bottom-[19%]", size: 38, tilt: "-9deg",  color: "rgba(160,74,151,0.22)", float: "animate-sensa-sway",  delay: "0.7s" },
  { id: "e", icon: "folder", pos: "left-7 bottom-[11%]",  size: 28, tilt: "11deg",  color: "rgba(139,63,131,0.20)", float: "animate-sensa-sway-b", delay: "3.4s" },
];

const ALLOWED = ["pdf", "docx", "doc", "txt", "xlsx", "xls", "csv"];
const ACCEPT = ".pdf,.docx,.doc,.txt,.xlsx,.xls,.csv";

/* Only the states that WANT something get a filled chip.
 *
 * Every row used to carry one, so "Ready to use" — the outcome you hope for and
 * the one that needs no action — shouted as loudly as "Needs your review", and
 * the filenames underneath them came third. Reserving the fill means the eye
 * lands on the one document that is waiting on you, which is the only reason to
 * open this panel at all. */
const TONE = {
  ready:   { dot: "bg-[#2DAD4E]", text: "text-[#6B6472]", chip: "" },
  working: { dot: "bg-[#77707E]", text: "text-[#6B6472]", chip: "" },
  review:  { dot: "bg-[#A04A97]", text: "text-[#8E4187]", chip: "bg-[#A04A97]/10 px-1.5 py-[2px] rounded-full font-semibold" },
  failed:  { dot: "bg-[#DC2626]", text: "text-[#B91C1C]", chip: "bg-[#FEF2F2] px-1.5 py-[2px] rounded-full font-semibold" },
};

const prettySize = (bytes) => {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const FileIcon = () => (
  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3v5h5" />
    <path d="M19 8v11a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h8z" />
  </svg>
);

const Bar = ({ value, label, name }) => (
  <div className="mt-1.5">
    <div className="flex items-baseline justify-between">
      <span className="text-[11.5px] text-[#6E6775]">{label}</span>
      <span className="text-[11.5px] font-semibold tabular-nums text-[#A04A97]">{value}%</span>
    </div>
    <div
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${name} progress`}
      className="mt-1 h-[4px] overflow-hidden rounded-full bg-[#18101C]/[0.06]"
    >
      <div
        className="h-full rounded-full bg-gradient-to-r from-[#A04A97] to-[#C86B4F] transition-[width] duration-500 ease-out motion-reduce:transition-none"
        style={{ width: `${Math.max(value, 4)}%` }}
      />
    </div>
  </div>
);

/* A file that exists on screen before it exists on the server.
 *
 * The upload used to show nothing until the POST resolved — the button read
 * "Uploading…" at the bottom of the panel and the list stayed empty, then a row
 * appeared already half-processed. Showing the row immediately makes the wait
 * legible, and it is what the project table does. */
const PendingRow = ({ file, progress }) => (
  <li className="rounded-lg bg-[#FCFBFC] px-2 py-2">
    <div className="flex items-start gap-2.5">
      <span className="mt-[1px] flex-none text-[#A04A97]/50">
        <FileIcon />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-medium text-[#18101C]" title={file.name}>
          {file.name}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-[2px] text-[11.5px] font-semibold ${TONE.working}`}>
            <span className="h-2 w-2 animate-spin rounded-full border border-[#6B6472]/40 border-t-[#6B6472] motion-reduce:animate-none" />
            Uploading
          </span>
          {file.size ? (
            <span className="text-[11.5px] text-[#6E6775]">{prettySize(file.size)}</span>
          ) : null}
        </div>
        <Bar value={progress} label="Sending to the server…" name={file.name} />
      </div>
    </div>
  </li>
);

/* One document.
 *
 * Three of the four states are just a chip. The fourth — "needs your review" —
 * is a decision, and a decision needs its reason on screen: the summary the
 * extractor produced, then approve or remove. Burying that behind a trip to the
 * project page is what left documents uploaded from chat sitting untouched.
 *
 * Note there is no local "busy" flag. There used to be, and it never cleared:
 * it was set on click and only reset in the catch, so a SUCCESSFUL approve left
 * the row reading "Starting…" for ever. Pending state belongs to the panel,
 * which is what polls, and it clears itself when the server's status moves.
 */
const TrashIcon = () => (
  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

const Row = ({ doc, projectId, progress, pending, onApprove, onReject, onDelete }) => {
  const [open, setOpen] = useState(false);
  /* The delete icon reveals its actions rather than firing.
   *
   * They slide in over the row's own right edge, where the icon was — so the
   * confirmation appears where the click landed and reads as that control
   * opening, not as a new thing arriving somewhere else on the page. */
  const [confirming, setConfirming] = useState(false);
  /* The summary of a document that has already been approved.
   *
   * It is not in the list payload — that polls every few seconds and a summary
   * runs to kilobytes — so it is fetched the first time someone opens this row
   * and kept for as long as the row lives. */
  const [summary, setSummary] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryBusy, setSummaryBusy] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  // Kept mounted while it slides back out — unmounting on cancel is what made
  // the strip disappear instantly instead of leaving.
  const [leaving, setLeaving] = useState(false);
  const cancelRef = useRef(null);
  const reviewing = doc.tone === "review";
  const tone = TONE[doc.tone] || TONE.working;
  const busy = doc.tone === "working" || !!pending;
  const label =
    pending === "approve" ? "Starting…"
      : pending === "reject" ? "Removing…"
      : pending === "delete" ? "Deleting…"
      : doc.state;

  // A document awaiting review already has Remove, which does exactly this for
  // a document that was never ingested. Two controls that delete the same file
  // would only make the reader wonder how they differ.
  const canDelete = !!onDelete && !reviewing && !pending;

  // Offered once the document has settled. While one awaits review its summary
  // is already on screen in the block you act on, so a second route to the same
  // text would only make the reader wonder how the two differ.
  const canReadSummary = !reviewing && !pending && !!doc.has_summary;

  /* Opens on hover, and on FOCUS — hover alone would put this behind a gesture
   * a keyboard cannot make and a touchscreen does not have. Closing is delayed
   * a beat so the pointer can travel from the label into the panel without it
   * disappearing on the way. */
  const hoverTimer = useRef(null);

  const load = useCallback(() => {
    if (summary !== null || summaryBusy) return;   // fetched once, then kept
    setSummaryBusy(true);
    setSummaryError(null);
    api
      .get(`/api/v1/rfp/folders/${projectId}/copilot/files/${encodeURIComponent(doc.key || doc.id)}/summary/`)
      .then((res) => setSummary(res?.data?.summary || ""))
      .catch(() => setSummaryError("Couldn't load the summary."))
      .finally(() => setSummaryBusy(false));
  }, [summary, summaryBusy, projectId, doc.key, doc.id]);

  /* Opens on a PAUSE, not on contact.
   *
   * Now that the whole row is the trigger, a pointer crossing the list on its
   * way to the composer would otherwise fire a panel on every row it passed
   * over. 220ms is long enough that only stopping counts as asking. */
  const showSummary = useCallback(() => {
    clearTimeout(hoverTimer.current);
    if (summaryOpen) return;
    hoverTimer.current = setTimeout(() => {
      setSummaryOpen(true);
      load();
    }, 220);
  }, [summaryOpen, load]);

  // Closing gets its own, shorter grace so the pointer can cross the gap into
  // the panel without it disappearing on the way.
  const hideSummary = useCallback(() => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setSummaryOpen(false), 140);
  }, []);

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  // Opening puts focus on Cancel, so a keyboard lands on the safe action and
  // Escape backs out — the same two ways a mouse can leave this alone.
  useEffect(() => {
    if (confirming && !leaving) cancelRef.current?.focus();
  }, [confirming, leaving]);

  // Matches the conceal animation. Runs on cancel and on Escape, so both ways
  // out look the same.
  const dismiss = useCallback(() => {
    setLeaving(true);
    setTimeout(() => {
      setConfirming(false);
      setLeaving(false);
    }, 220);
  }, []);

  return (
    <li
      onKeyDown={(e) => {
        if (e.key === "Escape" && confirming && !leaving) {
          e.stopPropagation();
          dismiss();
        }
        if (e.key === "Escape" && summaryOpen) setSummaryOpen(false);
      }}
      /* The whole row reveals the summary — except over the delete control,
         which marks itself out. onMouseOver rather than onMouseEnter precisely
         because it BUBBLES: moving between children of the row re-fires it, so
         stepping onto the trash icon and back off it is noticed. */
      onMouseOver={(e) => {
        if (!canReadSummary || confirming) return;
        if (e.target.closest?.("[data-no-summary]")) hideSummary();
        else showSummary();
      }}
      onMouseLeave={hideSummary}
      // Keyboard reaches it the same way. Without this the summary would be
      // pointer-only, which is the failure that comes with hover interfaces.
      tabIndex={canReadSummary ? 0 : undefined}
      onFocus={canReadSummary && !confirming ? showSummary : undefined}
      onBlur={canReadSummary ? hideSummary : undefined}
      // No overflow-hidden here any more: the summary floats out past the row
      // and would be sliced off at its edge. Clipping moved inward, to the one
      // thing that needs it — the delete strip sliding in from the right.
      className={`relative px-2 py-3 transition-colors motion-reduce:transition-none ${
        confirming ? "bg-[#FCFBFC]" : "bg-[#FCFBFC] hover:bg-[#F6F5F6]"
      }`}
    >
      <div className="relative overflow-hidden">
      <div className="flex items-start gap-2.5">
        <span className="mt-[2px] flex-none text-[#A04A97]/70">
          <FileIcon />
        </span>
        <div className="min-w-0 flex-1">
          {/* Two lines, not one truncated one. RFP filenames lead with contract
              numbers and end with the part that identifies them, so cutting the
              tail removes exactly the half worth reading. */}
          <div className="flex items-start gap-2">
            <p className="line-clamp-2 min-w-0 flex-1 break-words text-[14.5px] font-medium leading-[1.35] text-[#18101C]" title={doc.name}>
              {doc.name}
            </p>
            {/* Quiet until wanted, but always present. Hover-only controls are
                controls most people never find — and this one being hard to
                find is not a safety feature, since the confirm step is. */}
            {canDelete && !confirming && (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                data-no-summary
                onFocus={hideSummary}
                aria-label={`Delete ${doc.name}`}
                title="Delete this document"
                aria-expanded={confirming}
                className="-mr-1 -mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-md text-[#6E6775] transition-colors hover:bg-[#FEF2F2] hover:text-[#B91C1C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
              >
                <TrashIcon />
              </button>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className={`inline-flex items-center gap-1.5 text-[12px] ${tone.text} ${tone.chip}`}>
              {busy ? (
                <span className="h-2 w-2 flex-none animate-spin rounded-full border border-[#6B6472]/40 border-t-[#6B6472] motion-reduce:animate-none" />
              ) : (
                <span className={`h-[5px] w-[5px] flex-none rounded-full ${tone.dot}`} />
              )}
              {label}
            </span>
            {doc.size && <span className="text-[12px] text-[#6E6775]">{doc.size}</span>}
            {doc.questionnaire && (
              <span className="rounded-full bg-[#A04A97]/10 px-1.5 py-[2px] text-[11.5px] font-semibold text-[#8E4187]">
                Questionnaire
              </span>
            )}
          </div>

          {/* Real progress from the pipeline, not a guess. Only while working —
              a bar sitting at a number nothing is moving reads as stuck. */}
          {doc.tone === "working" && typeof progress === "number" && !pending && (
            <Bar value={progress} label={doc.progress_label || "Working…"} name={doc.name} />
          )}

          {reviewing && !pending && (
            <div className="mt-2.5 rounded-xl bg-[#A04A97]/[0.05] p-3">
              {doc.summary ? (
                <>
                  <p className={`text-[13px] leading-[1.6] text-[#3F3F46] ${open ? "" : "line-clamp-3"}`}>
                    {doc.summary}
                  </p>
                  <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    className="mt-1.5 text-[12.5px] font-semibold text-[#A04A97] transition-colors hover:text-[#8E4187] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
                  >
                    {open ? "Show less" : "Show more"}
                  </button>
                </>
              ) : (
                /* The buttons stay live. Waiting for the summary is the normal
                 * case and it usually lands within seconds, but if that task
                 * ever fails, gating approval on it would leave the document
                 * permanently unusable with no way out. */
                <p className="flex items-center gap-2 text-[13px] leading-relaxed text-[#6B6472]">
                  <span className="h-3 w-3 flex-none animate-spin rounded-full border border-[#A04A97]/30 border-t-[#A04A97] motion-reduce:animate-none" />
                  Preparing the summary…
                </p>
              )}

              <div className="mt-2.5 flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => onApprove(doc)}
                  className="rounded-lg bg-[#A04A97] px-3 py-1.5 text-[12.5px] font-semibold text-white shadow-[0_1px_2px_rgba(160,74,151,0.3)] transition-colors hover:bg-[#8E4187] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2"
                >
                  Approve &amp; use
                </button>
                <button
                  type="button"
                  onClick={() => onReject(doc)}
                  className="rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium text-[#6B6472] transition-colors hover:bg-[#18101C]/[0.05] hover:text-[#3F3F46] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
                >
                  Remove
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* The actions, sliding in over the row's right edge.
          Cancel is the wider, plainer target and sits nearest the content;
          Delete is last, so the destructive one is never the button your hand
          is already over when the strip arrives. The gradient lets the filename
          pass under it instead of being cut off by a hard edge. */}
      {confirming && (
        <div className={`absolute inset-y-0 right-0 flex items-center gap-1.5 bg-gradient-to-l from-[#FCFBFC] via-[#FCFBFC] to-transparent pl-10 pr-3 motion-reduce:animate-none ${leaving ? "animate-sensa-conceal" : "animate-sensa-reveal"}`}>
          <button
            ref={cancelRef}
            type="button"
            onClick={dismiss}
            className="rounded-lg px-2.5 py-1.5 text-[12.5px] font-medium text-[#6B6472] transition-colors hover:bg-[#18101C]/[0.06] hover:text-[#3F3F46] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              setConfirming(false);
              onDelete(doc);
            }}
            aria-label={`Delete ${doc.name}`}
            className="rounded-lg bg-[#B91C1C] px-3 py-1.5 text-[12.5px] font-semibold text-white shadow-[0_1px_2px_rgba(185,28,28,0.25)] transition-colors hover:bg-[#991B1B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B91C1C] focus-visible:ring-offset-2 motion-reduce:transition-none"
          >
            Delete
          </button>
        </div>
      )}
      </div>

      {/* Floats over the rows below rather than pushing them down.
          Hover is a pointer resting somewhere; if the list reflowed under it the
          row would move out from under the cursor and close itself. Anchored to
          this row's left edge and held inside the panel's width. */}
      {summaryOpen && canReadSummary && (
        <div
          onMouseEnter={showSummary}
          onMouseLeave={hideSummary}
          className="absolute left-2 right-2 top-[calc(100%-6px)] z-20 rounded-xl border border-[#18101C]/[0.07] bg-white p-3 shadow-[0_2px_6px_rgba(24,16,28,0.06),0_16px_32px_-16px_rgba(24,16,28,0.28)] animate-copilot-rise motion-reduce:animate-none"
        >
          {summaryBusy ? (
            <p className="flex items-center gap-2 text-[13px] text-[#6B6472]">
              <span className="h-3 w-3 flex-none animate-spin rounded-full border border-[#A04A97]/30 border-t-[#A04A97] motion-reduce:animate-none" />
              Reading it back…
            </p>
          ) : summaryError ? (
            <p role="alert" className="text-[13px] text-[#B91C1C]">{summaryError}</p>
          ) : summary ? (
            <>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.07em] text-[#8E4187]">
                What this document covers
              </p>
              <p className="max-h-[42vh] overflow-y-auto whitespace-pre-wrap text-[13.5px] leading-[1.65] tracking-[-0.003em] text-[#3F3F46] sensa-scroll">
                {summary}
              </p>
            </>
          ) : (
            <p className="text-[13px] italic text-[#6B6472]">
              No summary was generated for this document.
            </p>
          )}
        </div>
      )}
    </li>
  );
};

const CopilotProjectPanel = ({ projectId, projectName, visible, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(false);
  // Files chosen but not yet acknowledged by the server, plus the batch upload
  // percentage. One percentage, not one per file: a multipart POST reports bytes
  // for the whole request, and inventing per-file numbers from that would be a
  // guess dressed up as measurement.
  const [queued, setQueued] = useState([]);
  const [sent, setSent] = useState(0);
  // Documents we have acted on and are waiting for the server to catch up with.
  // Held here rather than in the row because THIS is what polls: a row that owns
  // its own pending flag cannot keep the panel awake, which is exactly why an
  // approved document used to sit at "Starting…" for ever.
  const [acting, setActing] = useState({}); // { [key]: "approve" | "reject" }

  const inputRef = useRef(null);
  /* Highest percentage each file has reached, so it can never be shown going
   * backwards. Keyed by name because that is the one identity the placeholder
   * and the real row share — the placeholder has no document id yet. Belt and
   * braces on top of the shared scale: the pipeline itself reports
   * ready_for_review as 100 and then ingesting as 50, so a monotonic floor is
   * the only thing that makes a bar honest across every path through it. */
  const floors = useRef({});

  const pct = useCallback((name, phase, value) => {
    const raw =
      phase === "transfer"
        ? (value * TRANSFER_SHARE) / 100
        : TRANSFER_SHARE + (value * (100 - TRANSFER_SHARE)) / 100;
    const next = Math.max(floors.current[name] || 0, Math.min(100, Math.round(raw)));
    floors.current[name] = next;
    return next;
  }, []);

  const load = useCallback(
    async (quiet) => {
      if (!projectId) return null;
      if (!quiet) setLoading(true);
      try {
        const res = await api.get(`/api/v1/rfp/folders/${projectId}/copilot/files/`);
        setData(res.data);
        setError(null);
        return res.data;
      } catch (err) {
        setError(err?.response?.data?.msg || "Couldn't load this project's documents.");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [projectId]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const docs = useMemo(() => data?.documents || [], [data]);

  /* Optimistic rows, minus any the server has already told us about.
   *
   * Clearing `queued` after the refresh is not enough on its own: setData()
   * inside load() flushes in an earlier microtask than the cleanup that follows
   * the await, so for one paint the file was on screen twice — once as the
   * optimistic row at 100% and once as the real one at whatever the pipeline had
   * reached. Deriving it instead means the placeholder disappears in the very
   * render that introduces its replacement, with no window in between.
   *
   * Matched on name because that is what the server stores; a file it rejected
   * never arrives, so its placeholder is cleared by the cleanup a moment later
   * alongside the error explaining why. */
  const placeholders = useMemo(
    () => queued.filter((f) => !docs.some((d) => d.name === f.name)),
    [queued, docs]
  );

  /* Approval starts a genuinely new phase, so the bar starts again with it.
   *
   * Without this the floor set while the document was being READ carries into
   * indexing: extraction ends around 90%, ingestion reports 50, the monotonic
   * clamp holds it at 90, and the bar sits frozen for the whole of the second
   * phase — the stuck-looking bar the clamp exists to prevent, reintroduced by
   * the clamp itself. Anything not currently working has no bar on screen, so
   * dropping its floor is invisible and costs nothing. */
  useEffect(() => {
    for (const d of docs) {
      if (d.tone !== "working") delete floors.current[d.name];
    }
  }, [docs]);

  /* Clear a pending flag once the server's own status has moved off what it was
   * when we acted. Approving briefly leaves the document reporting
   * 'ready_for_review' — the project screen has the same race — so clearing on
   * the response instead would flash the Approve buttons back for a beat. */
  useEffect(() => {
    setActing((prev) => {
      if (!Object.keys(prev).length) return prev;
      const next = { ...prev };
      let changed = false;
      for (const [key, entry] of Object.entries(prev)) {
        const doc = docs.find((d) => String(d.key || d.id) === key);
        const expired = Date.now() - entry.at > ACTION_TIMEOUT_MS;
        if (!doc) {
          // Rejected and deleted documents vanish; that is the completion signal.
          if (entry.kind === "reject" || entry.kind === "delete" || expired) {
            delete next[key];
            changed = true;
          }
        } else if (expired || (entry.kind === "approve" && doc.tone !== "review")) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [docs]);

  /* Keep polling while ANYTHING is unfinished — the server's own `settled` flag
   * only counts documents it is actively processing, and an approval that has
   * not landed yet is neither settled nor visible to it. Trusting `settled`
   * alone is what stopped the clock the instant someone pressed Approve. */
  /* A document reaches review before its summary is written — that runs as its
   * own task afterwards. The server calls the document settled either way, so
   * without this the panel stops polling exactly when the one thing still coming
   * is the text the user needs in order to decide. */
  const awaitingSummary = docs.some((d) => d.tone === "review" && !d.summary);
  const busy = Object.keys(acting).length > 0 || queued.length > 0 || awaitingSummary;
  useEffect(() => {
    if (!data) return undefined;
    if (data.settled && !busy) return undefined;
    const t = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(t);
  }, [data, busy, load]);

  const addFiles = async (fileList) => {
    const picked = Array.from(fileList || []);
    const files = picked.filter((f) => ALLOWED.includes(String(f.name).split(".").pop().toLowerCase()));
    const bad = picked.filter((f) => !files.includes(f));
    if (bad.length) {
      setError(
        `${bad.map((f) => f.name).join(", ")} — only PDF, Word, Excel, CSV and text files can be read.`
      );
    }
    if (!files.length) return;
    if (files.length > MAX_PER_UPLOAD) {
      // Refused whole rather than truncated to the first five: silently
      // dropping files the user selected is worse than asking them again.
      setError(
        `${files.length} files selected — ${MAX_PER_UPLOAD} at a time is the limit. ` +
          `Add the rest straight after.`
      );
      return;
    }

    // On screen first, then sent. This is the whole point of the change.
    setQueued(files);
    setSent(0);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      // Same endpoint the project screen posts to — the server owns duplicate
      // detection, format checks, storage and the ingestion dispatch.
      const res = await api.post(`/api/v1/rfp/folders/${projectId}/upload/`, form, {
        onUploadProgress: (e) => {
          if (e.total) setSent(Math.min(99, Math.round((e.loaded / e.total) * 100)));
        },
      });
      setSent(100);
      // A 202 does not mean everything landed: the endpoint accepts some files
      // and rejects others in the same call.
      const rejected = (res?.data?.uploads || []).filter((r) => r.status === "failed");
      if (rejected.length) {
        setError(rejected.map((r) => `${r.file} — ${r.reason || r.error}`).join("  ·  "));
      }
      await load(true);
    } catch (err) {
      setError(err?.response?.data?.msg || "The upload didn't go through.");
    } finally {
      // Dropped only after the refresh, so the real rows are already in place
      // and the list never blinks empty between the two.
      setQueued([]);
      setSent(0);
    }
  };

  const approve = async (doc) => {
    const key = String(doc.key || doc.id);
    setActing((p) => ({ ...p, [key]: { kind: "approve", at: Date.now() } }));
    setError(null);
    try {
      await api.post(`/api/v1/rfp/folders/${projectId}/documents/${key}/ingest/`, {
        action: "approve",
      });
      await load(true);
    } catch (err) {
      setActing((p) => {
        const n = { ...p };
        delete n[key];
        return n;
      });
      setError(err?.response?.data?.error || "Couldn't start ingestion.");
    }
  };

  const reject = async (doc) => {
    const key = String(doc.key || doc.id);
    setActing((p) => ({ ...p, [key]: { kind: "reject", at: Date.now() } }));
    setError(null);
    try {
      await api.delete(`/api/v1/rfp/folders/${projectId}/documents/${key}/reject/`);
      await load(true);
    } catch (err) {
      setActing((p) => {
        const n = { ...p };
        delete n[key];
        return n;
      });
      setError(err?.response?.data?.error || "Couldn't remove that document.");
    }
  };

  /* Delete for good.
   *
   * The same endpoint the project screen uses, so a document cannot be
   * deletable in one place and not the other — and folder-share permission
   * stays enforced by the code that already does it, rather than by a second
   * rule written here that could drift from it.
   */
  const remove = useCallback(
    async (doc) => {
      const key = String(doc.key || doc.id);
      setActing((p) => ({ ...p, [key]: { kind: "delete", at: Date.now() } }));
      setError(null);
      try {
        await api.delete("/api/v1/rfp/documents/delete/", {
          data: { doc_ids: [doc.id], folder_ids: [] },
        });
      } catch (err) {
        setActing((p) => {
          const n = { ...p };
          delete n[key];
          return n;
        });
        setError(
          err?.response?.data?.error ||
            err?.response?.data?.msg ||
            "Couldn't delete that document."
        );
        return;
      }
      await load(true);
    },
    [load]
  );

  const counts = data?.counts;
  const title = data?.project_name || projectName || "Project";
  const href = data?.organization_id
    ? `/rfp-projects/project/${projectId}/org/${data.organization_id}/project-files`
    : `/rfp-projects/project/${projectId}/project-files`;

  const uploading = queued.length > 0;
  /* "3 documents · 1 to review" put both facts in the same grey, so the only
   * part that asks anything of the reader was the easiest to miss. The counts
   * that need a person are coloured; the total is context. */
  const totalLabel = counts ? `${counts.total} document${counts.total === 1 ? "" : "s"}` : "Loading…";

  return (
    <CopilotSidePanel visible={visible} label={`${title} — documents`} onClose={onClose}>
          {/* Ambient depth behind the panel.
           *
           * Two heavily blurred brand-tinted blooms drifting on deliberately
           * mismatched cycles, so they never fall into step and start reading
           * as a loop. They sit at a few percent opacity — enough that the
           * surface is alive in peripheral vision while someone waits on an
           * upload, nowhere near enough to compete with a filename. Anything
           * livelier behind a list you have to READ would be decoration that
           * costs legibility, which is a bad trade in a working panel.
           *
           * pointer-events-none so it can never intercept a click, and the
           * whole layer is dropped under prefers-reduced-motion rather than
           * frozen — a static blob serves no purpose. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 overflow-hidden motion-reduce:hidden"
          >
            {/* Soft colour, for depth. Blurred to 3xl, so there is no edge for
                the eye to track — this layer is felt, not watched. */}
            <span className="absolute -left-16 top-8 h-56 w-56 rounded-full bg-[#A04A97]/[0.07] blur-3xl animate-copilot-drift-a" />
            <span className="absolute -right-20 bottom-24 h-64 w-64 rounded-full bg-[#C86B4F]/[0.06] blur-3xl animate-copilot-drift-c" />

            {/* The objects — the layer you actually see move.
                Blurred orbs alone never read as motion; a sharp glyph bobbing
                20px is unmissable, which is the same reason the welcome screen
                pairs the two. These are the SAME motifs, imported rather than
                redrawn, so the panel and the welcome screen stay one product.

                Held to the extreme left and right margins and the very bottom,
                where the list has no text — a crisp line behind a filename is
                the one thing this layer must never cost. */}
            {PANEL_GLYPHS.map((g) => (
              <span
                key={g.id}
                className={`absolute ${g.pos} ${g.float}`}
                style={{ animationDelay: g.delay, color: g.color }}
              >
                {/* The tilt lives on the svg, not the wrapper, so the wrapper
                    keeps owning the drift animation — putting both transforms
                    on one element makes the animation overwrite the rotation. */}
                <svg
                  aria-hidden="true"
                  width={g.size}
                  height={g.size}
                  viewBox="0 0 24 24"
                  style={{ transform: `rotate(${g.tilt})` }}
                  {...PANEL_STROKE}
                >
                  {PANEL_ICONS[g.icon]}
                </svg>
              </span>
            ))}

            {/* Dissolves everything toward the middle, where the rows live. */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_78%_58%_at_50%_50%,rgba(252,251,252,0.92)_0%,rgba(252,251,252,0.55)_58%,transparent_100%)]" />
          </div>

          <header className="relative z-10 flex items-start gap-2 border-b border-[#18101C]/[0.07] bg-[#FCFBFC] px-4 pb-3.5 pt-4">
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-[16px] font-semibold tracking-[-0.01em] text-[#18101C]" title={title}>
                {title}
              </h2>
              <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-[12.5px] text-[#6E6775]">
                <span>{totalLabel}</span>
                {!!counts?.review && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span className="font-semibold text-[#8E4187]">{counts.review} to review</span>
                  </>
                )}
                {!!counts?.working && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{counts.working} processing</span>
                  </>
                )}
                {!!counts?.failed && (
                  <>
                    <span aria-hidden="true">·</span>
                    <span className="font-semibold text-[#B91C1C]">{counts.failed} failed</span>
                  </>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close project panel"
              className="flex h-7 w-7 flex-none items-center justify-center rounded-lg text-[#6B6472] transition-colors hover:bg-[#18101C]/[0.06] hover:text-[#18101C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
            >
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </header>

          {/* The whole list is the drop target, not just a button. Dragging a
              file at a panel full of files and finding nowhere to let go is a
              small, avoidable indignity. */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              if (!uploading) setDragging(true);
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              if (!uploading) addFiles(e.dataTransfer.files);
            }}
            className={`sensa-scroll relative min-h-0 flex-1 overflow-y-auto px-2 py-2 transition-colors ${
              dragging ? "bg-[#A04A97]/[0.06]" : ""
            } motion-reduce:transition-none`}
          >
            {dragging && (
              <div className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center rounded-xl border-2 border-dashed border-[#A04A97] bg-white/70">
                <p className="text-[14px] font-semibold text-[#A04A97]">Drop to add</p>
              </div>
            )}

            {loading && !data ? (
              <ul className="space-y-2 px-2 py-1" aria-hidden="true">
                {[0, 1, 2].map((i) => (
                  <li key={i} className="h-11 animate-pulse rounded-lg bg-[#18101C]/[0.05]" />
                ))}
              </ul>
            ) : placeholders.length || docs.length ? (
              <ul className="divide-y divide-[#18101C]/[0.05]">
                {/* Newest first, matching the server's order — a file you just
                    dropped belongs at the top where you are looking. */}
                {placeholders.map((f) => (
                  <PendingRow
                    key={`${f.name}:${f.size}`}
                    file={f}
                    progress={pct(f.name, "transfer", sent)}
                  />
                ))}
                {docs.map((d) => (
                  <Row
                    key={d.id}
                    doc={d}
                    projectId={projectId}
                    progress={
                      typeof d.progress === "number" ? pct(d.name, "server", d.progress) : null
                    }
                    pending={acting[String(d.key || d.id)]?.kind}
                    onApprove={approve}
                    onReject={reject}
                    onDelete={remove}
                  />
                ))}
              </ul>
            ) : (
              <div className="px-3 py-8 text-center">
                <p className="text-[14px] font-medium text-[#18101C]">No documents yet</p>
                <p className="mt-1 text-[13px] leading-relaxed text-[#5F5866]">
                  Drop the RFP here, or use the button below, and I'll be able to answer
                  questions about it.
                </p>
              </div>
            )}
          </div>

          {error && (
            <p role="alert" className="border-t border-[#18101C]/[0.07] px-4 py-2 text-[12.5px] leading-relaxed text-[#B91C1C]">
              {error}
            </p>
          )}

          {/* The footer is a surface, not a strip with a border on it.
           *
           * It was one hairline rule and two flat controls sitting directly on
           * the panel background — which is why it read as unfinished next to
           * everything above it. Now it lifts: a soft wash upward, a hairline
           * highlight along the very top edge to catch the light, and a blur so
           * the list dissolves into it as it scrolls under rather than stopping
           * dead at a line. */}
          <footer className="relative z-10 flex items-center gap-2 border-t border-[#18101C]/[0.06] bg-gradient-to-b from-white/70 to-white px-4 py-3.5 shadow-[0_-1px_0_rgba(255,255,255,0.9)_inset,0_-8px_20px_-16px_rgba(24,16,28,0.18)] backdrop-blur-[6px]">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              /* Depth instead of a flat fill — the same treatment the user's own
                 message bubble uses, so the one primary action in this panel
                 belongs to the same product as the chat beside it. Light falls
                 from the top-left, a hairline inset highlight defines the edge,
                 and the shadow is split: a tight contact shadow plus a wide soft
                 one, rather than a single blur doing both jobs badly.

                 The ramp stops at #A64E9D. The next step up the brand scale
                 drops white text below 4.5:1, and a gradient is only as legible
                 as its brightest point. */
              className="group/add inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-[#A64E9D] to-[#8E4187] px-3.5 py-2 text-[13.5px] font-semibold text-white ring-1 ring-inset ring-white/[0.16] shadow-[0_1px_2px_rgba(24,16,28,0.10),0_8px_18px_-10px_rgba(142,65,135,0.65)] transition-[transform,box-shadow,filter] duration-200 hover:-translate-y-[1px] hover:brightness-[1.04] hover:shadow-[0_2px_4px_rgba(24,16,28,0.10),0_12px_24px_-10px_rgba(142,65,135,0.7)] active:translate-y-0 active:brightness-95 disabled:cursor-wait disabled:translate-y-0 disabled:opacity-60 disabled:shadow-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] focus-visible:ring-offset-2 motion-reduce:transition-none motion-reduce:hover:transform-none"
            >
              {/* Disabled alone is not legible — the button looked identical
                  while an upload was in flight, so it read as unresponsive
                  rather than busy. */}
              {uploading ? (
                <span className="h-4 w-4 animate-spin rounded-full border-[2px] border-white/40 border-t-white motion-reduce:animate-none" />
              ) : (
                /* An upload glyph, not a bare plus. "+" is the generic sign for
                   add-anything; this button adds FILES, and the tray-and-arrow
                   says so before the label is read. Drawn at the same 1.9 weight
                   and rounded caps as the file and trash icons so the panel has
                   one icon language rather than three. */
                <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-200 group-hover/add:-translate-y-[1px] motion-reduce:transition-none motion-reduce:group-hover/add:transform-none">
                  <path d="M12 16V4" />
                  <path d="M8 8l4-4 4 4" />
                  <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                </svg>
              )}
              {uploading ? "Uploading…" : "Add documents"}
            </button>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = "";
              }}
            />

            {/* A real target, not floating text. It sat as bare words against
                the footer with no hover surface, so the only thing marking it
                as clickable was its colour — which is also what marks plain
                emphasis elsewhere in this panel. */}
            <Link
              to={href}
              className="group/link ml-auto inline-flex items-center gap-1.5 rounded-lg px-2.5 py-2 text-[13px] font-semibold text-[#A04A97] transition-colors hover:bg-[#A04A97]/[0.08] hover:text-[#8E4187] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
            >
              Open full project
              {/* Leaves-this-view, drawn properly. A lone diagonal arrow is the
                  same glyph this app uses for "up and to the right" trends; the
                  framed variant only ever means "opens elsewhere". */}
              <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-200 group-hover/link:translate-x-[1px] group-hover/link:-translate-y-[1px] motion-reduce:transition-none motion-reduce:group-hover/link:transform-none">
                <path d="M14 4h6v6" />
                <path d="M20 4l-8 8" />
                <path d="M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" />
              </svg>
            </Link>
          </footer>
    </CopilotSidePanel>
  );
};

export default CopilotProjectPanel;
